// frontend/src/PatientReports.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import './PatientReports.css';

const POLL_MS = 2 * 60 * 1000;

// 0 means no date cutoff. Reports stay visible as long as they exist in Azure Blob or the DB.
const SCAN_WINDOW_HOURS = 0;

// Toggle to show/hide the "Include reviewed" checkbox.
const SHOW_INCLUDE_REVIEWED = false;

export default function PatientReports({ patientId, active, onUnreadChange, onCountsChange }) {
  const { t, i18n } = useTranslation();
  const locale = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState('');
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const pollRef = useRef(null);
  const reqRef = useRef(null);

  const pushUnread = useCallback(
    (val) => {
      onUnreadChange?.(val);
      onCountsChange?.({ unread: val });
    },
    [onUnreadChange, onCountsChange]
  );

  const abortInFlight = useCallback(() => {
    if (reqRef.current) {
      try {
        reqRef.current.abort?.();
      } catch {}
      reqRef.current = null;
    }
  }, []);

  const fetchReports = useCallback(async () => {
    if (!patientId) return;

    abortInFlight();
    const controller = new AbortController();
    reqRef.current = controller;

    try {
      setLoading(true);
      setError('');

      const listParams = {
        scan: 1,
        since_hours: SCAN_WINDOW_HOURS,
      };

      // If this UI is hidden, do not hard-filter to unread.
      // That allows reviewed reports to remain visible in the Reports tab.
      if (SHOW_INCLUDE_REVIEWED) {
        if (!includeReviewed) {
          listParams.status = 'unread';
        }
      }

      const [listRes, summaryRes] = await Promise.all([
        axios.get(`patients/${patientId}/reports/`, {
          params: listParams,
          signal: controller.signal,
        }),
        axios.get(`patients/${patientId}/reports/summary/`, {
          params: {
            scan: 1,
            since_hours: SCAN_WINDOW_HOURS,
          },
          signal: controller.signal,
        }),
      ]);

      const items = Array.isArray(listRes.data)
        ? listRes.data
        : Array.isArray(listRes.data?.results)
          ? listRes.data.results
          : [];

      const normalized = items.map((r) => {
        const dbId = r.id ?? r.report_id ?? null;
        const blobName = r.blob_name || null;
        const blobUrl = r.blob_url || null;
        const filename = r.filename || (blobName ? String(blobName).split('/').pop() : 'report.pdf');
        const createdAt = r.created_at || null;
        const locationName = r.location_name || null;

        return {
          id: dbId,
          filename,
          blob_url: blobUrl,
          blob_name: blobName,
          created_at: createdAt,
          location_name: locationName,
          acknowledged_at: r.acknowledged_at ?? null,
        };
      });

      setReports(normalized);

      const unread = Number(summaryRes?.data?.unread ?? 0);
      pushUnread(unread);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;

      console.error('Failed to load reports', e);
      setError(t('reports.loadFailed'));
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, includeReviewed, pushUnread, abortInFlight]);

  useEffect(() => {
    fetchReports();

    return () => {
      abortInFlight();
    };
  }, [fetchReports, abortInFlight]);

  useEffect(() => {
    if (!active) return;

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchReports, POLL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      abortInFlight();
    };
  }, [active, fetchReports, abortInFlight]);

  const isSameReport = (a, b) =>
    !!a &&
    !!b &&
    (
      (a.id && b.id && a.id === b.id) ||
      (a.blob_name && b.blob_name && a.blob_name === b.blob_name) ||
      (a.blob_url && b.blob_url && a.blob_url === b.blob_url) ||
      (a.filename && b.filename && a.filename === b.filename)
    );

  const acknowledgeSmart = async (item) => {
    try {
      if (item.id) {
        await axios.post(`patients/${patientId}/reports/${item.id}/acknowledge/`);

        setReports((prev) => {
          const updated = prev.map((r) =>
            isSameReport(r, item)
              ? { ...r, acknowledged_at: new Date().toISOString() }
              : r
          );

          return SHOW_INCLUDE_REVIEWED && !includeReviewed
            ? updated.filter((r) => !isSameReport(r, item))
            : updated;
        });

        window.dispatchEvent(new Event('cti:report-acknowledged'));
        fetchReports();
        return;
      }

      const body = {
        blob_name: item.blob_name || undefined,
        blob_url: item.blob_url || undefined,
        filename: item.filename || undefined,
        location_name: item.location_name || undefined,
        created_at: item.created_at || undefined,
      };

      const res = await axios.post(`patients/${patientId}/reports/acknowledge-by/`, body, {
        params: {
          since_hours: SCAN_WINDOW_HOURS,
        },
      });

      const ackRow = res?.data || {};

      setReports((prev) => {
        const updated = prev.map((r) =>
          isSameReport(r, item)
            ? {
                ...r,
                id: ackRow.id ?? ackRow.report_id ?? r.id,
                acknowledged_at: ackRow.acknowledged_at || new Date().toISOString(),
              }
            : r
        );

        return SHOW_INCLUDE_REVIEWED && !includeReviewed
          ? updated.filter((r) => !isSameReport(r, item))
          : updated;
      });

      window.dispatchEvent(new Event('cti:report-acknowledged'));
      fetchReports();
    } catch (e) {
      console.error('Failed to acknowledge report', e);
      const serverDetail = e?.response?.data?.detail;
      const isZh = locale === 'zh-CN';
      alert(!isZh && serverDetail ? serverDetail : t('reports.failedMarkReviewed'));
    }
  };

  return (
    <div className="pr-container">
      <div className="pr-controls">
        {SHOW_INCLUDE_REVIEWED && (
          <label>
            <input
              type="checkbox"
              checked={includeReviewed}
              onChange={(e) => {
                setIncludeReviewed(e.target.checked);
                setTimeout(fetchReports, 0);
              }}
            />{' '}
            {t('reports.includeReviewed')}
          </label>
        )}
      </div>

      {loading && <p>{t('reports.loading')}</p>}
      {!loading && error && <p className="pr-error">{error}</p>}
      {!loading && !error && !reports.length && <p>{t('reports.none')}</p>}

      {!loading && !error && !!reports.length && (
        <ul className="pr-list">
          {reports.map((r, idx) => {
            const key = r.id || r.blob_name || r.blob_url || `${r.filename}-${idx}`;
            const isUnread = !r.acknowledged_at;

            return (
              <li
                key={key}
                className={`pr-item ${isUnread ? 'pr-item--unread' : 'pr-item--read'}`}
              >
                <div className="pr-left">
                  <span className={`pr-dot ${isUnread ? 'pr-dot--unread' : 'pr-dot--read'}`} />

                  {r.blob_url ? (
                    <a
                      href={r.blob_url}
                      target="_blank"
                      rel="noreferrer"
                      className="pr-link"
                    >
                      {r.filename}
                    </a>
                  ) : (
                    <span className="pr-link">{r.filename}</span>
                  )}

                  <span className="pr-date">
                    {r.created_at ? new Date(r.created_at).toLocaleString(locale) : ''}
                  </span>

                  {isUnread && (
                    <span className="pr-badge pr-badge--unreviewed" title={t('reports.notReviewed')}>
                      {t('reports.notReviewed')}
                    </span>
                  )}
                </div>

                <div className="pr-right">
                  <button
                    className="pr-btn"
                    onClick={() => acknowledgeSmart(r)}
                    title={t('reports.markReviewed')}
                  >
                    {t('reports.markReviewed')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}